// THE PUBLIC URL SHAPE — one place, because the host decided it for us.
//
// OBSERVED ON THE LIVE HOST, 2026-09-06. Replit's static host resolves a
// directory index by REDIRECTING to the trailing-slash form:
//
//   GET https://capnolog.com/features   → 301 → /features/
//   GET https://capnolog.com/features/  → 200  (the prerendered document)
//
// …for every one of the 54 non-root routes. The `from = "/features"` rewrite
// rows in .replit-artifact/artifact.toml do NOT prevent that redirect (the
// host's directory redirect fires first), and a Replit rewrite cannot express
// an HTTP redirect, so this is not something the artifact can configure away.
//
// The site was previously rendering the SLASH-LESS form everywhere: every
// <link rel="canonical">, every og:url, every sitemap <loc> and every internal
// <a href>. The result, in Search Console, was 16 pages filed under "Page with
// redirect" and 1 under "Duplicate, Google chose different canonical" — every
// submitted URL redirected, and every served page's canonical pointed at a URL
// that redirected back to it.
//
// SO THE CANONICAL PUBLIC URL OF EVERY NON-ROOT ROUTE IS THE TRAILING-SLASH
// FORM. The root stays "/". The ROUTE IDENTITIES — ROUTES, STATIC_ROUTES,
// REWRITES, SITEMAP[].path, the wouter <Route path>s, __PRERENDER_PATH__ and
// the on-disk layout — all stay slash-LESS, exactly as they were. Only the
// RENDERED public URLs change, and they change here.
//
// This module has NO imports on purpose. It is read by the browser bundle, by
// the SSR bundle, and (through src/routes.ts, which re-exports it) by plain
// Node gate scripts, so it must stay pure data-shaped like routes.ts itself.

/**
 * A route identity ("/features", "/help/logging", "/") → the URL the site
 * publishes for it ("/features/", "/help/logging/", "/").
 *
 * Idempotent: publicPath(publicPath(r)) === publicPath(r).
 */
export function publicPath(route: string): string {
  const trimmed = route.replace(/\/+$/, "");
  return trimmed === "" ? "/" : `${trimmed}/`;
}

/**
 * The same rule applied to an arbitrary internal href, hash and query intact.
 * This is what every link render site passes its href through, so the built
 * HTML and the hydrated DOM agree byte-for-byte and no internal click ever
 * costs a redirect.
 *
 *   "/pricing"            → "/pricing/"
 *   "/pricing#faq"        → "/pricing/#faq"
 *   "/download?x=1"       → "/download/?x=1"
 *   "/help/logging/"      → "/help/logging/"      (idempotent)
 *
 * LEFT ALONE, and why:
 *   • "/" and "/#beta"                 — the root is already canonical.
 *   • "#beta", "mailto:", "tel:", "https://…", "//cdn…" — not a path on this site.
 *   • anything whose last segment carries a file extension ("/llms.txt",
 *     "/sitemap.xml", "/opengraph.png", "/assets/x.js") — a FILE is not a
 *     directory, and the host serves it at exactly that name.
 *   • "/.well-known/apple-app-site-association" — a dot-prefixed path is a
 *     well-known FILE served by an artifact.toml rewrite, not a page.
 *   • "/join/…" — the runtime invite shell (noindex, one document answers every
 *     slug). It is not in ROUTES, has no per-slug directory on disk, and its
 *     rewrite is the glob "/join/*"; adding a slash would ask the host for a
 *     directory that does not exist.
 */
export function publicHref(href: string): string {
  if (typeof href !== "string" || href === "") return href;
  // Not a root-relative path: external, protocol-relative, hash-only, mailto:,
  // tel:, capnolog: — none of them name a page on this site.
  if (!href.startsWith("/") || href.startsWith("//")) return href;
  // Dot-prefixed well-known files.
  if (href.startsWith("/.")) return href;

  const cut = href.search(/[?#]/);
  const path = cut === -1 ? href : href.slice(0, cut);
  const rest = cut === -1 ? "" : href.slice(cut);

  if (path === "/" || path === "") return href;
  if (path === "/join" || path.startsWith("/join/")) return href;
  if (path.endsWith("/")) return href;
  // A file, not a page: the LAST segment carries an extension.
  const lastSegment = path.slice(path.lastIndexOf("/") + 1);
  if (lastSegment.includes(".")) return href;

  return `${path}/${rest}`;
}

/**
 * True for an href the built HTML is allowed to carry as a same-origin link.
 * `verify-crawl.mjs` asserts this over every <a href> in every built document,
 * which is the durable half of this contract: the source-level rule is "route
 * every link through publicHref", and this is the check that notices when a new
 * literal skipped it.
 */
export function isPublicHref(href: string): boolean {
  return publicHref(href) === href;
}
